import Big from 'big.js';
import { first, isEmpty, toNumber } from 'lodash-es';
import { v1 as uuid } from 'uuid';

import { useFeatureFlag, useRoleAuth } from '@src/hooks';
import { useCustomers } from '@src/hooks/useCustomer';
import { useCustomerBalance, useCustomerMarketAccounts, type UpdateCustomerBalanceProps } from '@src/providers';
import {
  ACTION,
  Box,
  Button,
  ButtonVariants,
  Checkbox,
  CurrencyTagEnum,
  CustomerBalance,
  CustomerLedgerUpdateTypeEnum,
  DateTimePicker,
  Divider,
  DrawerContent,
  DrawerFooter,
  Flex,
  FormControlSizes,
  FormGroup,
  HStack,
  IconButton,
  IconName,
  Input,
  NOW,
  NotificationInternalToast,
  NotificationVariants,
  NumberInput,
  SearchSelect,
  Text,
  ToggleGroup,
  Transfer,
  VStack,
  formattedDateForSubscription,
  getDefaultCustomerMarketAccount,
  useCurrenciesContext,
  useDisclosureWithContext,
  useGlobalToasts,
  useMarketAccountsContext,
} from '@talos/kyoko';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BalanceTransactionPreview } from './BalanceTransactionPreview';
import type { CustomerTransactionForm } from './types';
import { useBalanceTransactionValidation } from './useBalanceTransactionValidation';
import { resolveCustomerMarketAccountsForBalanceDrawer } from './utils';

export interface LegacyBalanceTransactionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  entity?: CustomerBalance | Transfer;
  transactionType?: CustomerLedgerUpdateTypeEnum;
  suppressHeader?: boolean;
}

const TRANSACTION_TYPES = [CustomerLedgerUpdateTypeEnum.Deposit, CustomerLedgerUpdateTypeEnum.Withdraw];
const FORCE_WITHDRAW_CONFIRM_TEXT = 'confirm';

export function LegacyBalanceTransactionDrawer({
  entity,
  isOpen,
  transactionType,
  onClose,
  suppressHeader,
}: LegacyBalanceTransactionDrawerProps) {
  // Extract customerBalance and transfer from entity
  const customerBalance = entity instanceof CustomerBalance ? entity : undefined;
  const transfer = entity instanceof Transfer ? entity : undefined;

  const { add: addToast } = useGlobalToasts();
  const [form, setForm] = useState<CustomerTransactionForm>({});
  const { errors, touched, setTouched, setAllTouched } = useBalanceTransactionValidation(form);
  const customers = useCustomers();
  const customerBalanceService = useCustomerBalance();
  const { isAuthorized } = useRoleAuth();
  const {
    customerBalanceShowForceWithdraw,
    enableTransactTimeInNewBalanceTransaction,
    customerBalanceRequireForceWithdrawConfirm,
    disableCustomerMarketAccounts,
  } = useFeatureFlag();
  const { currenciesList, currenciesBySymbol } = useCurrenciesContext();
  const { marketAccountsByCounterparty, marketAccountsByName } = useMarketAccountsContext();

  const currencyInfo = form.Currency ? currenciesBySymbol.get(form.Currency) : undefined;
  const showTxHashField = !currencyInfo?.Tags?.includes(CurrencyTagEnum.Fiat);

  const [isForceWithdraw, setIsForceWithdraw] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const showForceWithdrawSection = useMemo(() => {
    return (
      isAuthorized(ACTION.FORCE_WITHDRAWAL) &&
      customerBalanceShowForceWithdraw &&
      form.Type === CustomerLedgerUpdateTypeEnum.Withdraw
    );
  }, [customerBalanceShowForceWithdraw, form.Type, isAuthorized]);

  const currencySymbols = useMemo(() => {
    return currenciesList?.map(currency => currency.Symbol) ?? [];
  }, [currenciesList]);

  const { customerMarketAccountsByName, getActiveCustomerMarketAccounts, getCustomerMarketAccounts } =
    useCustomerMarketAccounts();
  const customerAccountsByName = disableCustomerMarketAccounts ? marketAccountsByName : customerMarketAccountsByName;

  const resolvedCustomerMarketAccounts = useMemo(() => {
    return resolveCustomerMarketAccountsForBalanceDrawer({
      customer: form.Counterparty,
      disableCustomerMarketAccounts,
      getActiveCustomerMarketAccounts,
      marketAccountsByCounterparty,
    });
  }, [form.Counterparty, disableCustomerMarketAccounts, getActiveCustomerMarketAccounts, marketAccountsByCounterparty]);

  const customerMarketAccounts = useMemo(
    () => resolvedCustomerMarketAccounts.map(ma => ma.Name),
    [resolvedCustomerMarketAccounts]
  );

  // Auto-select market account if only one is available
  useEffect(() => {
    if (customerMarketAccounts.length === 1) {
      setForm(prev => ({ ...prev, MarketAccount: first(customerMarketAccounts) }));
    }
  }, [customerMarketAccounts]);

  // Initialize form when drawer opens
  useEffect(() => {
    if (isOpen) {
      const newForm: CustomerTransactionForm = { Type: transactionType };

      if (customerBalance) {
        Object.assign(newForm, {
          Counterparty: customerBalance.Counterparty,
          MarketAccount: customerBalance.MarketAccount,
          Currency: customerBalance.Currency,
          OutstandingBuy: customerBalance.OutstandingBuy,
          OutstandingSell: customerBalance.OutstandingSell,
        });
      } else if (transfer && 'TransferID' in transfer) {
        Object.assign(newForm, {
          Amount: transfer.Amount,
          Currency: transfer.Currency,
          ExternalID: transfer.TransferID,
          TransferID: transfer.TransferID,
        });
      }

      setTouched({});
      setForm(newForm);
      setIsForceWithdraw(false);
      setConfirmText('');
    }
  }, [customerBalance, isOpen, setTouched, transactionType, transfer]);

  const updateForm = useCallback(
    (target: keyof CustomerTransactionForm, value: string | Date | undefined = '') => {
      if (target === 'Type') {
        // Reset Force Withdraw State when Type updates
        setIsForceWithdraw(false);
        setConfirmText('');
      }

      // Empty strings should store as undefined
      const finalValue = value === '' ? undefined : value;
      setForm(prev => ({ ...prev, [target]: finalValue }));

      // Mark field as touched
      setTouched(prev => ({ ...prev, [target]: true }));
    },
    [setTouched]
  );

  const handleOnGenerateID = useCallback(() => {
    updateForm('ExternalID', uuid());
  }, [updateForm]);

  const handleCounterpartyUpdate = useCallback(
    (newCounterparty?: string) => {
      if (!newCounterparty) {
        setForm(prev => ({ ...prev, MarketAccount: undefined }));
        return;
      }

      // Set form.MarketAccount to default or first active account for the counterparty
      const accountsForDefault = disableCustomerMarketAccounts
        ? resolveCustomerMarketAccountsForBalanceDrawer({
            customer: newCounterparty,
            disableCustomerMarketAccounts,
            getActiveCustomerMarketAccounts,
            marketAccountsByCounterparty,
          })
        : getCustomerMarketAccounts(newCounterparty);
      const newMktAcc = getDefaultCustomerMarketAccount(accountsForDefault)?.Name;

      setForm(prev => ({ ...prev, MarketAccount: newMktAcc }));
      updateForm('Counterparty', newCounterparty);
    },
    [
      disableCustomerMarketAccounts,
      getActiveCustomerMarketAccounts,
      getCustomerMarketAccounts,
      marketAccountsByCounterparty,
      updateForm,
    ]
  );

  const handleUpdateCustomerBalance = useCallback(
    async (balanceData: UpdateCustomerBalanceProps) => {
      return customerBalanceService
        .updateCustomerBalance(balanceData)
        .then(() => {
          addToast({
            text: 'Successfully updated balance.',
            variant: NotificationVariants.Positive,
          });
        })
        .catch((e: ErrorEvent) => {
          addToast({
            text: e?.toString() || 'Could not update balance.',
            variant: NotificationVariants.Negative,
          });
        });
    },
    [addToast, customerBalanceService]
  );

  const handleNewBalanceTransaction = useCallback(() => {
    if (form.Type === CustomerLedgerUpdateTypeEnum.Set) {
      return handleUpdateCustomerBalance(form as UpdateCustomerBalanceProps);
    }

    // Convert form to the format expected by the API
    const { TransactTime, ...formWithoutTransactTime } = form;
    const apiForm = {
      ...formWithoutTransactTime,
      ...(TransactTime != null && { TransactTime: formattedDateForSubscription(TransactTime) }),
    };

    return customerBalanceService
      .createBalanceTransaction(apiForm, isForceWithdraw, false) // Legacy drawer always uses regular endpoints
      .then(() => {
        onClose();
        addToast({
          text: 'Successfully created new balance transaction.',
          variant: NotificationVariants.Positive,
        });
      })
      .catch((e: ErrorEvent) => {
        addToast({
          text: e?.toString() || 'Could not add new balance transaction.',
          variant: NotificationVariants.Negative,
        });
      });
  }, [addToast, customerBalanceService, isForceWithdraw, form, handleUpdateCustomerBalance, onClose]);

  const verifyPastedValueIsNumber = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedValue = e.clipboardData.getData('text');
      const numberValue = toNumber(pastedValue);

      if (isNaN(numberValue)) {
        e.preventDefault();
        addToast({
          text: `Invalid Amount: ${pastedValue}`,
          variant: NotificationVariants.Negative,
        });
      }
    },
    [addToast]
  );

  const title = transfer ? 'New Customer Deposit' : 'New Balance Transaction';
  const confirmLabel = transfer ? 'Preview Deposit' : 'Preview Transaction';
  const isConfirmDisabled =
    !isEmpty(errors) ||
    (isForceWithdraw && customerBalanceRequireForceWithdrawConfirm && confirmText !== FORCE_WITHDRAW_CONFIRM_TEXT);
  const selectedTransactionType = TRANSACTION_TYPES.find(type => type === form.Type);

  const previewDisclosure = useDisclosureWithContext<{ form: CustomerTransactionForm; isForceWithdraw: boolean }>();

  const onCloseDrawer = useCallback(() => {
    previewDisclosure.close();
    onClose();
  }, [onClose, previewDisclosure]);

  const hasOutstandingOrders = useMemo(() => {
    return !Big(form.OutstandingBuy || 0).eq(0) || !Big(form.OutstandingSell || 0).eq(0);
  }, [form.OutstandingBuy, form.OutstandingSell]);

  const dateTimePickerValue = useMemo(() => {
    if (form.TransactTime instanceof Date) {
      return form.TransactTime;
    }
    return form.TransactTime ? new Date(form.TransactTime) : null;
  }, [form.TransactTime]);

  if (!customers || !isOpen) {
    return null;
  }

  return (
    <Box h="100%" overflow="auto">
      {!suppressHeader && (
        <>
          <Flex alignItems="center" justifyContent="space-between" w="100%" p="spacing16">
            <Text color="colorTextImportant" size="fontSizeLarge">
              {title}
            </Text>
            <IconButton
              icon={IconName.Close}
              onClick={onCloseDrawer}
              aria-label="Close"
              data-testid="balance-transaction-drawer-close-button"
              ghost={true}
            />
          </Flex>
          <Divider />
        </>
      )}

      <DrawerContent>
        <VStack gap="spacing16" justifyContent="flex-start">
          {/* If setting balance as Talos User, show this warning */}
          {form.Type === CustomerLedgerUpdateTypeEnum.Set && (
            <Box>
              <Text color="colorTextWarning">
                Setting a customer balance can impact balances if the customer is actively trading. Confirm this
                customer has no open orders.
              </Text>
            </Box>
          )}

          <Box textAlign="left" w="100%">
            <HStack gap="spacing16">
              <Box flexBasis="50%" textAlign="left">
                <FormGroup
                  mb="spacing16"
                  label="Customer*"
                  error={touched.Counterparty && errors.Counterparty ? errors.Counterparty : undefined}
                >
                  <SearchSelect
                    options={customers}
                    selection={customers.find(c => c?.Name === form.Counterparty)}
                    getLabel={customer => customer.DisplayName}
                    onChange={c => handleCounterpartyUpdate(c?.Name)}
                    data-testid="balance-transaction-drawer-counterparty-select"
                  />
                </FormGroup>
              </Box>
              <Box flexBasis="50%" textAlign="left">
                <FormGroup
                  mb="spacing16"
                  label="Customer Account*"
                  error={touched.MarketAccount && errors.MarketAccount ? errors.MarketAccount : undefined}
                >
                  <SearchSelect
                    dropdownWidth={300}
                    selection={form.MarketAccount}
                    options={customerMarketAccounts}
                    getLabel={ma => customerAccountsByName.get(ma)?.SourceAccountID || ma}
                    onChange={ma => updateForm('MarketAccount', ma)}
                    disabled={customerMarketAccounts.length === 1 || form.Counterparty == null}
                    data-testid="balance-transaction-drawer-market-account-select"
                  />
                </FormGroup>
              </Box>
            </HStack>
            <FormGroup mb="spacing16" error={touched.Type && errors.Type ? errors.Type : undefined}>
              <Flex w="100%">
                <ToggleGroup<CustomerLedgerUpdateTypeEnum>
                  options={TRANSACTION_TYPES.map(type => ({
                    value: type,
                    displayName: type,
                    buttonVariant: ButtonVariants.Priority,
                    disabled: transfer != null,
                    testId: `balance-transaction-drawer-${type.toLowerCase()}-button`,
                  }))}
                  value={selectedTransactionType}
                  onChange={type => updateForm('Type', type)}
                  boxProps={{ w: '100%' }}
                  data-testid="balance-transaction-drawer-type-toggle"
                />
              </Flex>
            </FormGroup>

            <Divider mt="spacing16" mb="spacing16" />
            <Box mb="spacing16">
              <Text color="colorTextImportant">Transfer Details</Text>
            </Box>

            <HStack gap="spacing16">
              <Box flexBasis="50%">
                <FormGroup
                  mb="spacing16"
                  label="Currency*"
                  error={touched.Currency && errors.Currency ? errors.Currency : undefined}
                >
                  <SearchSelect
                    data-testid="balance-transaction-drawer-currency-select"
                    selection={form.Currency}
                    options={currencySymbols}
                    onChange={c => {
                      updateForm('Currency', c);
                      updateForm('TxHash', ''); // Target currency might not be FIAT, remove TxHash property
                    }}
                    getLabel={c => c}
                  />
                </FormGroup>
              </Box>
              <Box flexBasis="50%">
                <FormGroup
                  mb="spacing16"
                  label="Amount*"
                  error={touched.Amount && errors.Amount ? errors.Amount : undefined}
                >
                  <NumberInput
                    min="0"
                    value={form.Amount ?? ''}
                    suffix={form.Currency}
                    onChange={value => updateForm('Amount', value)}
                    autoFocus={!!form.Type}
                    data-testid="balance-transaction-drawer-amount-input"
                    onPaste={verifyPastedValueIsNumber}
                  />
                </FormGroup>
              </Box>
            </HStack>

            <FormGroup mb="spacing16" label="Comments">
              <Input
                value={form.Comments ?? ''}
                autoComplete="off"
                onChange={e => updateForm('Comments', e.target.value)}
                placeholder="Optional"
              />
            </FormGroup>

            <FormGroup mb="spacing16" label="External Comments">
              <Input
                value={form.ExternalComments ?? ''}
                autoComplete="off"
                onChange={e => updateForm('ExternalComments', e.target.value)}
                placeholder="Optional"
              />
            </FormGroup>

            {enableTransactTimeInNewBalanceTransaction && (
              <FormGroup mb="spacing16" label="Transaction Time">
                <DateTimePicker
                  portalize={false}
                  shortcuts={{ [NOW]: 'Now' }}
                  onChange={date => updateForm('TransactTime', date || undefined)}
                  value={dateTimePickerValue}
                  data-testid="balance-transaction-drawer-transact-time"
                  placeholder="Optional"
                />
              </FormGroup>
            )}

            <FormGroup
              mb="spacing16"
              label="External ID*"
              error={touched.ExternalID && errors.ExternalID ? errors.ExternalID : undefined}
            >
              <Input
                value={form.ExternalID ?? ''}
                autoComplete="off"
                onChange={e => updateForm('ExternalID', e.target.value)}
                data-testid="balance-transaction-drawer-external-id-input"
                placeholder="Required"
                maxLength={36}
                suffix={
                  <Button
                    size={FormControlSizes.Small}
                    variant={ButtonVariants.Default}
                    onClick={handleOnGenerateID}
                    ghost
                  >
                    Generate
                  </Button>
                }
              />
            </FormGroup>

            {showTxHashField && (
              <FormGroup
                mb="spacing16"
                label="TxHash"
                error={touched.TxHash && errors.TxHash ? errors.TxHash : undefined}
              >
                <Input
                  value={form.TxHash ?? ''}
                  autoComplete="off"
                  onChange={e => updateForm('TxHash', e.target.value)}
                  data-testid="balance-transaction-drawer-tx-hash-input"
                  placeholder="TxHash"
                />
              </FormGroup>
            )}

            {transfer != null && (
              <FormGroup mb="spacing16" label="Transfer">
                <Input value={transfer.Description} autoComplete="off" readOnly={true} placeholder="Optional" />
              </FormGroup>
            )}
          </Box>
          {/* If setting balance and either OutstandingBuy or OutstandingSell is not 0, show this warning */}
          {form.Type === CustomerLedgerUpdateTypeEnum.Set && hasOutstandingOrders && (
            <Text color="colorTextWarning">This customer has an active order impacting this currency.</Text>
          )}

          {showForceWithdrawSection && (
            <>
              <Divider />
              <Box data-testid="force-withdraw-section" alignSelf="flex-start">
                <Flex gap="spacing16">
                  <Text>Force Withdraw</Text>
                  <Checkbox
                    checked={isForceWithdraw}
                    onChange={e => setIsForceWithdraw(e.target.checked)}
                    data-testid="balance-transaction-drawer-force-withdraw-checkbox"
                  />
                </Flex>
                {isForceWithdraw && (
                  <Box mt="spacing8">
                    <NotificationInternalToast variant={NotificationVariants.Negative} textAlign="left">
                      Initiating a forced withdrawal bypasses all validations. Please use this option with caution.
                    </NotificationInternalToast>
                    {customerBalanceRequireForceWithdrawConfirm && (
                      <VStack w="100%" mt="spacing8">
                        <FormGroup label='To confirm, type "confirm" below:' w="100%">
                          <Input
                            value={confirmText}
                            autoComplete="off"
                            onChange={e => setConfirmText(e.target.value)}
                            data-testid="balance-transaction-drawer-confirm-input"
                          />
                        </FormGroup>
                      </VStack>
                    )}
                  </Box>
                )}
              </Box>
            </>
          )}
        </VStack>
      </DrawerContent>

      <DrawerFooter>
        <Button
          onClick={() => previewDisclosure.open({ form, isForceWithdraw })}
          variant={ButtonVariants.Primary}
          disabled={isConfirmDisabled}
          onMouseEnter={setAllTouched}
        >
          {confirmLabel}
        </Button>
      </DrawerFooter>

      <BalanceTransactionPreview
        previewDisclosure={previewDisclosure}
        onConfirmTransaction={handleNewBalanceTransaction}
      />
    </Box>
  );
}
